import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PaymentPage.css';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'https://douane.vtymconsultinggroup.com/wp-json';

const PaymentPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [inscription, setInscription] = useState(null);
    const [bankInfo, setBankInfo] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState('');
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState(false);
    const [paymentData, setPaymentData] = useState(null);
    const [telephone, setTelephone] = useState('');
    const [preuveFile, setPreuveFile] = useState(null);
    const [error, setError] = useState('');

    useEffect(() => {
        loadInscription();
        loadBankInfo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Charger les coordonnées bancaires (configurées dans wp-admin)
    const loadBankInfo = async () => {
        try {
            const response = await fetch(`${API_URL}/douane/v1/payment/bank-info`);
            const data = await response.json();
            if (data.success) {
                setBankInfo(data);
            }
        } catch (err) {
            console.error('Erreur chargement coordonnées bancaires:', err);
        }
    };

    // Charger les détails de l'inscription
    const loadInscription = async () => {
        try {
            const url = `${API_URL}/douane/v1/inscriptions/${id}`;
            console.log('🔍 Chargement inscription depuis:', url);

            const response = await fetch(url);
            console.log('📡 Statut réponse:', response.status);

            const data = await response.json();
            console.log('📦 Données reçues:', data);

            if (data.success) {
                setInscription(data.inscription);
            } else {
                setError('Inscription non trouvée');
            }
        } catch (err) {
            console.error('❌ Erreur chargement inscription:', err);
            setError('Impossible de charger l\'inscription');
        } finally {
            setLoading(false);
        }
    };

    // Initier paiement Orange Money
    const handleOrangeMoney = async () => {
        if (!telephone || telephone.length < 8) {
            setError('Veuillez entrer un numéro valide');
            return;
        }

        setProcessing(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/douane/v1/payment/orange-money/init`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    inscription_id: id,
                    montant: inscription.prix_total,
                    telephone: telephone
                })
            });

            const data = await response.json();

            if (data.success) {
                setPaymentData(data);
            } else {
                setError('Erreur lors de l\'initialisation du paiement');
            }
        } catch (err) {
            console.error('Erreur Orange Money:', err);
            setError('Impossible d\'initier le paiement Orange Money');
        } finally {
            setProcessing(false);
        }
    };

    // Initier paiement Wave
    const handleWave = async () => {
        if (!telephone || telephone.length < 8) {
            setError('Veuillez entrer un numéro valide');
            return;
        }

        setProcessing(true);
        setError('');

        try {
            const response = await fetch(`${API_URL}/douane/v1/payment/wave/init`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    inscription_id: id,
                    montant: inscription.prix_total,
                    telephone: telephone
                })
            });

            const data = await response.json();

            if (data.success) {
                setPaymentData(data);
            } else {
                setError('Erreur lors de l\'initialisation du paiement');
            }
        } catch (err) {
            console.error('Erreur Wave:', err);
            setError('Impossible d\'initier le paiement Wave');
        } finally {
            setProcessing(false);
        }
    };

    // Upload preuve virement
    const handleVirement = async () => {
        if (!preuveFile) {
            setError('Veuillez sélectionner une preuve de paiement');
            return;
        }

        setProcessing(true);
        setError('');

        const formData = new FormData();
        formData.append('inscription_id', id);
        formData.append('montant', inscription.prix_total);
        formData.append('preuve', preuveFile);

        try {
            const response = await fetch(`${API_URL}/douane/v1/payment/virement/upload`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                alert('Preuve de virement envoyée ! Validation sous 24h.');
                navigate('/dashboard');
            } else {
                setError('Erreur lors de l\'envoi de la preuve');
            }
        } catch (err) {
            console.error('Erreur Virement:', err);
            setError('Impossible d\'envoyer la preuve de virement');
        } finally {
            setProcessing(false);
        }
    };

    // Vérifier le statut du paiement
    const checkPaymentStatus = async (transactionId) => {
        try {
            const response = await fetch(`${API_URL}/douane/v1/payment/${transactionId}/status`);
            const data = await response.json();

            if (data.success && data.statut === 'valide') {
                alert('✅ Paiement confirmé !');
                navigate('/dashboard');
            } else {
                alert('⏳ Paiement en cours de traitement...');
            }
        } catch (err) {
            console.error('Erreur vérification:', err);
            alert('Impossible de vérifier le statut');
        }
    };

    const formatPrix = (prix) => {
        if (!prix) return '0';
        return parseInt(prix).toLocaleString('fr-FR');
    };

    if (loading) {
        return (
            <div className="payment-page">
                <div className="container">
                    <div className="loading">Chargement...</div>
                </div>
            </div>
        );
    }

    if (error && !inscription) {
        return (
            <div className="payment-page">
                <div className="container">
                    <div className="error-container">
                        <h2>❌ Erreur</h2>
                        <p>{error}</p>
                        <button onClick={() => navigate('/formations')} className="btn-back">
                            Retour aux formations
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!inscription) {
        return (
            <div className="payment-page">
                <div className="container">
                    <div className="error-container">
                        <h2>❌ Inscription non trouvée</h2>
                        <button onClick={() => navigate('/formations')} className="btn-back">
                            Retour aux formations
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-page">
            <div className="container">

                <div className="payment-container">

                    {/* Résumé de la commande */}
                    <div className="order-summary">
                        <h2>📋 Résumé de la commande</h2>
                        <div className="summary-item">
                            <span>Formation</span>
                            <strong>{inscription.formation?.titre || 'Formation'}</strong>
                        </div>
                        <div className="summary-item">
                            <span>Type</span>
                            <strong>{inscription.type === 'entreprise' ? 'Entreprise' : 'Particulier'}</strong>
                        </div>
                        {inscription.type === 'entreprise' && inscription.nombre_participants && (
                            <div className="summary-item">
                                <span>Participants</span>
                                <strong>{inscription.nombre_participants}</strong>
                            </div>
                        )}
                        <div className="summary-item">
                            <span>Email</span>
                            <strong>{inscription.email}</strong>
                        </div>
                        <div className="summary-item total">
                            <span>TOTAL</span>
                            <strong className="amount">{formatPrix(inscription.prix_total)} FCFA</strong>
                        </div>
                    </div>

                    {/* Méthodes de paiement */}
                    <div className="payment-methods">
                        <h2>💳 Choisissez votre mode de paiement</h2>

                        {error && (
                            <div className="error-message">
                                ⚠️ {error}
                            </div>
                        )}

                        {/* Orange Money */}
                        <div className="payment-method-card">
                            <div className="method-header">
                                <input
                                    type="radio"
                                    name="payment-method"
                                    id="orange-money"
                                    checked={selectedMethod === 'orange'}
                                    onChange={() => setSelectedMethod('orange')}
                                />
                                <label htmlFor="orange-money">
                                    <span className="method-icon">🟠</span>
                                    <span className="method-name">Orange Money CI</span>
                                </label>
                            </div>

                            {selectedMethod === 'orange' && !paymentData && (
                                <div className="method-form">
                                    <input
                                        type="tel"
                                        placeholder="Numéro Orange Money (ex: 0707123456)"
                                        value={telephone}
                                        onChange={(e) => setTelephone(e.target.value)}
                                        className="input-phone"
                                    />
                                    <button
                                        onClick={handleOrangeMoney}
                                        disabled={processing}
                                        className="btn-pay"
                                    >
                                        {processing ? 'Traitement...' : 'Générer le QR Code'}
                                    </button>
                                </div>
                            )}

                            {selectedMethod === 'orange' && paymentData && (
                                <div className="qr-code-section">
                                    <h3>Scannez ce QR Code avec Orange Money</h3>
                                    <img src={paymentData.qr_code_url} alt="QR Code Orange Money" className="qr-code" />
                                    <p className="instructions">{paymentData.instructions}</p>
                                    <button
                                        onClick={() => checkPaymentStatus(paymentData.transaction_id)}
                                        className="btn-verify"
                                    >
                                        Vérifier le paiement
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Wave */}
                        <div className="payment-method-card">
                            <div className="method-header">
                                <input
                                    type="radio"
                                    name="payment-method"
                                    id="wave"
                                    checked={selectedMethod === 'wave'}
                                    onChange={() => setSelectedMethod('wave')}
                                />
                                <label htmlFor="wave">
                                    <span className="method-icon">🌊</span>
                                    <span className="method-name">Wave CI</span>
                                </label>
                            </div>

                            {selectedMethod === 'wave' && !paymentData && (
                                <div className="method-form">
                                    <input
                                        type="tel"
                                        placeholder="Numéro Wave (ex: 0507123456)"
                                        value={telephone}
                                        onChange={(e) => setTelephone(e.target.value)}
                                        className="input-phone"
                                    />
                                    <button
                                        onClick={handleWave}
                                        disabled={processing}
                                        className="btn-pay"
                                    >
                                        {processing ? 'Traitement...' : 'Générer le QR Code'}
                                    </button>
                                </div>
                            )}

                            {selectedMethod === 'wave' && paymentData && (
                                <div className="qr-code-section">
                                    <h3>Scannez ce QR Code avec Wave</h3>
                                    <img src={paymentData.qr_code_url} alt="QR Code Wave" className="qr-code" />
                                    <p className="wave-number">Numéro Wave : {paymentData.wave_number}</p>
                                    <p className="instructions">{paymentData.instructions}</p>
                                    <button
                                        onClick={() => checkPaymentStatus(paymentData.transaction_id)}
                                        className="btn-verify"
                                    >
                                        Vérifier le paiement
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Virement Bancaire */}
                        <div className="payment-method-card">
                            <div className="method-header">
                                <input
                                    type="radio"
                                    name="payment-method"
                                    id="virement"
                                    checked={selectedMethod === 'virement'}
                                    onChange={() => setSelectedMethod('virement')}
                                />
                                <label htmlFor="virement">
                                    <span className="method-icon">🏦</span>
                                    <span className="method-name">Virement Bancaire</span>
                                </label>
                            </div>

                            {selectedMethod === 'virement' && (
                                <div className="method-form">
                                    <div className="bank-details">
                                        <h4>Coordonnées bancaires</h4>
                                        <p><strong>Banque :</strong> {bankInfo?.banque || 'Chargement...'}</p>
                                        <p><strong>IBAN :</strong> {bankInfo?.iban || 'Chargement...'}</p>
                                        <p><strong>Titulaire :</strong> {bankInfo?.titulaire || 'Chargement...'}</p>
                                        <p><strong>Montant :</strong> {formatPrix(inscription.prix_total)} FCFA</p>
                                    </div>

                                    <div className="file-upload">
                                        <label>Joindre la preuve de paiement (PDF, JPG, PNG)</label>
                                        <input
                                            type="file"
                                            accept="image/*,application/pdf"
                                            onChange={(e) => setPreuveFile(e.target.files[0])}
                                            className="input-file"
                                        />
                                        {preuveFile && (
                                            <span className="file-name">✓ {preuveFile.name}</span>
                                        )}
                                    </div>

                                    <button
                                        onClick={handleVirement}
                                        disabled={processing || !preuveFile}
                                        className="btn-pay"
                                    >
                                        {processing ? 'Envoi...' : 'Envoyer la preuve'}
                                    </button>
                                </div>
                            )}
                        </div>

                    </div>

                </div>

            </div>
        </div>
    );
};

export default PaymentPage;